import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Eye, ExternalLink, ShoppingCart } from "lucide-react";
import OrderDialog from "@/components/OrderDialog";
import { useState, useCallback, memo } from "react";
import { trackSocialClick, trackProductAction } from "@/lib/analytics";
import art001 from "@/assets/art-001.jpg";
import art002 from "@/assets/art-002.jpg"; 
import art003 from "@/assets/art-003.jpg";
import art004 from "@/assets/art-004.jpg";
import art005 from "@/assets/art-005.jpg";
import elegantnost from "@/assets/elegantnost.jpg";
import tenSkakuna from "@/assets/ten-skakuna.jpg";
import tigr from "@/assets/tigr.jpg";
import koshelekIliZhizn from "@/assets/koshelek-ili-zhizn.jpg";
import nochnoyPolet from "@/assets/nochnoy-polet.jpg";
import vesennySad from "@/assets/vesenny-sad.jpg";
import zmeya from "@/assets/zmeya.jpg";
import kolibri from "@/assets/kolibri.jpg";
import zolotoyLes from "@/assets/zolotoy-les.jpg";
import santaKlaus from "@/assets/santa-klaus.jpg";
import zolotoyVikhr from "@/assets/zolotoy-vikhr.jpg";
import peresechenieGranits from "@/assets/peresechenie-granits.jpg";
import naedine from "@/assets/naedine.jpg";
import kleopatraSoLvom from "@/assets/kleopatra-so-lvom.jpg";
import sestraMiloserdia from "@/assets/sestra-miloserdia.jpg";
import winter from "@/assets/winter.jpg";
import girlInDistance from "@/assets/girl-in-the-distance.jpg";
import wheatFields from "@/assets/wheat-fields.jpg";
import winterLandscape from "@/assets/winter-landscape.jpg";
import autumnForest from "@/assets/autumn-forest.jpg";
import mediterranean from "@/assets/mediterranean.jpg";
import cosmicEye from "@/assets/cosmic-eye.jpg";
import treeOnHill from "@/assets/tree-on-the-hill.jpg";
import fruits from "@/assets/fruits.jpg";
import luchSolntsa from "@/assets/luch-solntsa.jpg";
import luchSolntsaNew from "@/assets/luch-solntsa-new.jpg";
import winterUpdated from "@/assets/winter-updated.jpg";
import girlInDistanceUpdated from "@/assets/girl-in-the-distance-updated.jpg";
import wheatFieldsUpdated from "@/assets/wheat-fields-updated.jpg";
import winterLandscapeUpdated from "@/assets/winter-landscape-updated.jpg";
import autumnForestUpdated from "@/assets/autumn-forest-updated.jpg";
import mediterraneanUpdated from "@/assets/mediterranean-updated.jpg";
import cosmicEyeUpdated from "@/assets/cosmic-eye-updated.jpg";
import treeOnHillUpdated from "@/assets/tree-on-the-hill-updated.jpg";
import ostrovNew from "@/assets/ostrov-new.jpg";
import lavandovyePolyaNew from "@/assets/lavandovye-polya-new.jpg";
import matritsaNew from "@/assets/matritsa-new.jpg";
import goluboyNew from "@/assets/goluboy-new.jpg";
import slonyDaliNew from "@/assets/slony-dali-new.jpg";
import belyyParusNew from "@/assets/belyy-parus-new.jpg";
import birthOfStarNew from "@/assets/birth-of-star-new.jpg";
import zenit from "@/assets/zenit.jpg";
import vetreniDubrava from "@/assets/vetrenichka-dubravnaya.jpg";

interface ArtworkCardProps {
  id: string;
  title: string;
  category?: string;
  format: string;
  canvas: string;
  medium: string;
  size: string;
  price: number;
  image: string;
  description?: string;
  year?: number;
  status?: string;
  tags?: string[];
  vk_link?: string;
  website_link?: string;
}

// Маппинг изображений
const imageMap: Record<string, string> = {
  "/src/assets/art-001.jpg": art001,
  "/src/assets/art-002.jpg": art002,
  "/src/assets/art-003.jpg": art003,
  "/src/assets/art-004.jpg": art004,
  "/src/assets/art-005.jpg": art005,
  "/src/assets/elegantnost.jpg": elegantnost,
  "/src/assets/ten-skakuna.jpg": tenSkakuna,
  "/src/assets/tigr.jpg": tigr,
  "/src/assets/koshelek-ili-zhizn.jpg": koshelekIliZhizn,
  "/src/assets/nochnoy-polet.jpg": nochnoyPolet,
  "/src/assets/vesenny-sad.jpg": vesennySad,
  "/src/assets/zmeya.jpg": zmeya,
  "/src/assets/kolibri.jpg": kolibri,
  "/src/assets/zolotoy-les.jpg": zolotoyLes,
  "/src/assets/santa-klaus.jpg": santaKlaus,
  "/src/assets/zolotoy-vikhr.jpg": zolotoyVikhr,
  "/src/assets/peresechenie-granits.jpg": peresechenieGranits,
  "/src/assets/naedine.jpg": naedine,
  "/src/assets/kleopatra-so-lvom.jpg": kleopatraSoLvom,
  "/src/assets/sestra-miloserdia.jpg": sestraMiloserdia,
  "/src/assets/winter.jpg": winter,
  "/src/assets/girl-in-the-distance.jpg": girlInDistance,
  "/src/assets/wheat-fields.jpg": wheatFields,
  "/src/assets/winter-landscape.jpg": winterLandscape,
  "/src/assets/autumn-forest.jpg": autumnForest,
  "/src/assets/mediterranean.jpg": mediterranean,
  "/src/assets/cosmic-eye.jpg": cosmicEye,
  "/src/assets/tree-on-the-hill.jpg": treeOnHill,
  "/src/assets/fruits.jpg": fruits,
  "/src/assets/luch-solntsa.jpg": luchSolntsa,
  "/src/assets/luch-solntsa-new.jpg": luchSolntsaNew,
  "/src/assets/winter-updated.jpg": winterUpdated,
  "/src/assets/girl-in-the-distance-updated.jpg": girlInDistanceUpdated,
  "/src/assets/wheat-fields-updated.jpg": wheatFieldsUpdated,
  "/src/assets/winter-landscape-updated.jpg": winterLandscapeUpdated,
  "/src/assets/autumn-forest-updated.jpg": autumnForestUpdated,
  "/src/assets/mediterranean-updated.jpg": mediterraneanUpdated,
  "/src/assets/cosmic-eye-updated.jpg": cosmicEyeUpdated,
  "/src/assets/tree-on-the-hill-updated.jpg": treeOnHillUpdated,
  "/src/assets/ostrov-new.jpg": ostrovNew,
  "/src/assets/lavandovye-polya-new.jpg": lavandovyePolyaNew,
  "/src/assets/matritsa-new.jpg": matritsaNew,
  "/src/assets/goluboy-new.jpg": goluboyNew,
  "/src/assets/slony-dali-new.jpg": slonyDaliNew,
  "/src/assets/belyy-parus-new.jpg": belyyParusNew,
    "/src/assets/birth-of-star-new.jpg": birthOfStarNew,
    "/src/assets/zenit.jpg": zenit,
    "/src/assets/vetrenichka-dubravnaya.jpg": vetreniDubrava
};

const ArtworkCard = memo(({
  id, 
  title, 
  category,
  format, 
  canvas, 
  medium, 
  size, 
  price, 
  image, 
  description,
  year,
  status,
  tags,
  vk_link,
  website_link
}: ArtworkCardProps) => {
  // Получаем изображение с ПРИОРИТЕТОМ на хостинг
  const getImageSource = (imagePath: string) => {
    if (!imagePath) return art001;
    
    // ПРИОРИТЕТ 1: Если путь начинается с /images/ - загружаем НАПРЯМУЮ с хостинга
    if (imagePath.startsWith('/images/')) {
      return `https://w-wagner.art${imagePath}`;
    }
    
    // ПРИОРИТЕТ 2: Если путь из БД (/src/assets/) - конвертируем в хостинг
    if (imagePath.startsWith('/src/assets/')) {
      const fileName = imagePath.split('/').pop() || '';
      // Убираем расширение и ищем на хостинге (Vite добавляет хеши)
      const baseName = fileName.replace(/\.(jpg|jpeg|png|gif|webp)$/i, '');
      
      // Сначала проверяем локальный маппинг (для старых товаров)
      if (imageMap[imagePath]) {
        return imageMap[imagePath];
      }
      
      // Для новых товаров загружаем с хостинга
      // Формат: /images/filename-hash.jpg (Vite обрабатывает при билде)
      return `https://w-wagner.art/images/${fileName}`;
    }
    
    // ПРИОРИТЕТ 3: Проверяем точное соответствие в маппинге
    if (imageMap[imagePath]) {
      return imageMap[imagePath];
    }
    
    // ПРИОРИТЕТ 4: Извлекаем имя файла и пробуем найти в маппинге
    const fileName = imagePath.split('/').pop() || '';
    if (fileName) {
      const foundKey = Object.keys(imageMap).find(key => key.includes(fileName));
      if (foundKey) {
        return imageMap[foundKey];
      }
      
      // Последняя попытка - загрузить напрямую с хостинга
      return `https://w-wagner.art/images/${fileName}`;
    }
    
    // Fallback
    return art001;
  };
  
  const imageSource = getImageSource(image);
  const isAvailable = status !== "продано";
  const [orderDialogOpen, setOrderDialogOpen] = useState(false);

  // Получаем URL для артворка из website_link (ПРИОРИТЕТ) или fallback на id
  const getArtworkUrl = () => {
    // ПРИОРИТЕТ: Используем website_link из БД
    if (website_link && website_link.trim() !== '') {
      // Извлекаем путь, удаляя домен и протокол
      const path = website_link.replace(/^https?:\/\/[^\/]+/, '').replace(/^\//, '');
      return `/${path}`;
    }
    // FALLBACK: Используем старый формат raboty/id
    return `/raboty/${id}`;
  };

  const artworkUrl = getArtworkUrl();

  const handleOrderClick = useCallback(() => {
    setOrderDialogOpen(true);
    trackProductAction(id, 'order_click', title);
  }, [id, title]);

  const handleVKClick = useCallback(() => {
    trackSocialClick('VK', `product_${id}`, title);
    trackProductAction(id, 'vk_click', title);
  }, [id, title]);

  return (
    <Card className="gallery-card group relative backdrop-blur-sm bg-card/80 border-border/50 hover:border-primary/30 transition-all duration-500">
      <CardContent className="p-0">
        <div className="relative overflow-hidden aspect-[3/4] rounded-t-lg">
          <img
            src={imageSource}
            alt={title}
            className={`w-full h-full object-contain transition-all duration-700 group-hover:scale-105 group-hover:brightness-110 ${!isAvailable ? 'opacity-75 grayscale-[30%]' : ''}`}
            loading="lazy"
            width="400"
            height="533"
          />
          
          {/* Elegant overlay gradient */}
          <div className="absolute inset-0 bg-gradient-to-t from-background/95 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-all duration-500" />
          
          {/* Premium overlay controls */}
          <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-500 transform translate-y-2 group-hover:translate-y-0">
            <Button 
              asChild 
              size="sm" 
              className="bg-gradient-to-r from-primary to-primary-glow text-primary-foreground font-medium shadow-xl border-0 hover:shadow-2xl hover:from-primary-glow hover:to-primary transform hover:scale-105 transition-all duration-300"
              onClick={() => trackProductAction(id, 'view_click', title)}
            >
              <Link to={artworkUrl}>
                <Eye className="w-4 h-4 mr-2" />
                Просмотр
              </Link>
            </Button>
          </div>

          {/* Status Badge */}
          <div className="absolute top-3 right-3">
            <Badge 
              variant={isAvailable ? "default" : "destructive"} 
              className="shadow-xl backdrop-blur-sm font-medium"
            >
              {isAvailable ? "В наличии" : "Продано"}
            </Badge>
          </div>

          {/* Category Badge */}
          {category && (
            <div className="absolute top-3 left-3">
              <Badge variant="secondary" className="shadow-xl backdrop-blur-sm capitalize bg-background/80">
                {category}
              </Badge>
            </div>
          )}

          {/* Price tag in corner */}
          <div className="absolute bottom-3 right-3 opacity-0 group-hover:opacity-100 transition-all duration-500 transform translate-y-2 group-hover:translate-y-0">
            <div className="bg-primary/90 backdrop-blur-sm text-primary-foreground px-3 py-1 rounded-full text-sm font-bold shadow-xl">
              {price.toLocaleString('ru-RU')} ₽
            </div>
          </div>
        </div>
      </CardContent>
      
      <CardFooter className="p-5 bg-gradient-to-b from-card to-card/90">
        <div className="w-full space-y-4">
          <div className="space-y-2">
            <h3 className="font-bold text-xl mb-1 line-clamp-1 text-foreground group-hover:text-primary transition-colors duration-300">
              {title}
            </h3>
            {year && (
              <p className="text-sm text-muted-foreground font-medium">{year} год</p>
            )}
          </div>
          
          <div className="flex flex-wrap gap-2">
            <Badge variant="outline" className="text-xs font-medium border-border/50 bg-background/50 backdrop-blur-sm">
              {medium}
            </Badge>
            <Badge variant="outline" className="text-xs font-medium border-border/50 bg-background/50 backdrop-blur-sm">
              {format}
            </Badge>
            {size && (
              <Badge variant="outline" className="text-xs font-medium border-border/50 bg-background/50 backdrop-blur-sm">
                {size} см
              </Badge>
            )}
          </div>
          
          {description && (
            <p className="text-sm text-muted-foreground line-clamp-2 leading-relaxed font-light">
              {description}
            </p>
          )}
          
          <div className="flex items-center justify-between pt-4">
            <div className="flex-1">
              <span className="text-2xl font-bold text-primary block">
                {price.toLocaleString('ru-RU')} ₽
              </span>
            </div>
            <div className="flex gap-2 ml-2">
              {vk_link && (
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={handleVKClick}
                  className="border-blue-500/30 text-blue-600 hover:bg-blue-500 hover:text-white transition-all duration-300 font-medium"
                >
                  <a href={vk_link} target="_blank" rel="noopener noreferrer" className="flex items-center">
                    <ExternalLink className="w-3 h-3 mr-1" />
                    VK
                  </a>
                </Button>
              )}
              {isAvailable && (
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={handleOrderClick}
                  className="border-primary/30 text-primary hover:bg-primary hover:text-primary-foreground transition-all duration-300 font-medium"
                >
                  <ShoppingCart className="w-3 h-3 mr-1" />
                  Заказать
                </Button>
              )}
            </div>
          </div>
        </div>
      </CardFooter>
      
      {/* Order Dialog */}
      {isAvailable && (
        <OrderDialog 
          artworkTitle={title}
          artworkId={id}
          artworkPrice={price}
          open={orderDialogOpen}
          onOpenChange={setOrderDialogOpen}
        />
      )}
    </Card>
  );
});

export default ArtworkCard;